import {
  type IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import {useEnvironment} from '@wix/yoshi-flow-editor';
import classNames from 'classnames';
import React, {type CSSProperties, type FC, useEffect, useMemo, useState} from 'react';
import {A11y, Navigation} from 'swiper/modules';
import {Swiper, type SwiperClass, SwiperSlide} from 'swiper/react';
import stylesParams from '../../../../../components/ProductPage/stylesParams';
import {Alignment, ImageRatio} from '../../../../constants';
import {type ProvidedGlobalProps, withGlobalProps} from '../../../../providers/globalPropsProvider';
import {SPECS} from '../../../../specs';
import type {IMediaItem} from '../../../../types/app-types';
import {useProductGallery} from '../../ProductGalleryProvider/ProductGalleryProvider';
import {getImageRatio} from '../ProductGalleryLayout.cssVars';
import {ArrowButton} from './ArrowButton/ArrowButton';
import s from './ProductMediaSwiper.scss';
import {Thumbnail} from './Thumbnail/Thumbnail';
import {
  ARROW_BUTTON_SIZE,
  NEXT_ARROW_BUTTON_ID,
  PREV_ARROW_BUTTON_ID,
  SWIPER_SPEED,
  THUMBNAIL_BORDER_WIDTH,
  THUMBNAIL_GAP,
  THUMBNAIL_SIZE,
} from './utils/constants';
import {getArrowDirection} from './utils/getArrowDirection';
import {useHasVisibleArrows} from './utils/hooks/useHasVisibleArrows';
import {useSlidesOverflow} from './utils/hooks/useSlidesOverflow';
import {useSwiperNavigation} from './utils/hooks/useSwiperNavigation';
import {SwiperOptions} from 'swiper/types';

function computeActualRatio(
  thumbnailsRatio: number,
  isThumbnailsDesignViewerEnabled: boolean,
  thumbnailsImageRatio: number,
  media: IMediaItem[]
): number {
  if (
    isThumbnailsDesignViewerEnabled &&
    thumbnailsImageRatio === ImageRatio.AUTO &&
    media[0]?.width &&
    media[0]?.height
  ) {
    return media[0].width / media[0].height;
  }
  return getImageRatio(thumbnailsRatio);
}

function computeInlineCssVars(
  isThumbnailsDesignViewerEnabled: boolean,
  media: IMediaItem[],
  thumbnailsSize: number,
  thumbnailsSpacing: number,
  thumbnailsImageRatio: number
): Record<string, string> {
  if (isThumbnailsDesignViewerEnabled) {
    const singleThumbnailSize = thumbnailsSize + thumbnailsSpacing + thumbnailsImageRatio * 2;
    return {
      '--total-swiper-size-needed': `${media.length * singleThumbnailSize}px`,
      '--thumbnail-gap': `${thumbnailsSpacing}px`,
      '--thumbnail-border-width': `${thumbnailsImageRatio}px`,
      '--arrow-button-size': `${ARROW_BUTTON_SIZE}px`,
    };
  }
  const oldSingleThumbnailSize = THUMBNAIL_SIZE + THUMBNAIL_GAP + THUMBNAIL_BORDER_WIDTH * 2;
  return {
    '--total-swiper-size-needed': `${media.length * oldSingleThumbnailSize}px`,
    '--thumbnail-gap': `${THUMBNAIL_GAP}px`,
    '--thumbnail-border-width': `${THUMBNAIL_BORDER_WIDTH}px`,
    '--arrow-button-size': `${ARROW_BUTTON_SIZE}px`,
  };
}

function computeThumbnailDimensions(
  isThumbnailsDesignViewerEnabled: boolean,
  thumbnailsSize: number,
  thumbnailsImageRatio: number,
  media: IMediaItem[]
): {thumbnailsWidth: number; thumbnailsHeight: number} {
  if (!isThumbnailsDesignViewerEnabled) {
    return {thumbnailsWidth: THUMBNAIL_SIZE, thumbnailsHeight: THUMBNAIL_SIZE};
  }
  const actualRatio = computeActualRatio(thumbnailsImageRatio, true, thumbnailsImageRatio, media);
  return {thumbnailsWidth: thumbnailsSize, thumbnailsHeight: thumbnailsSize / actualRatio};
}

function computeSwiperWrapperClasses(isVertical: boolean, thumbnailsOverflow: boolean, align: Alignment): string {
  const allThumbnailsFitInView = !isVertical && !thumbnailsOverflow;
  const shouldCenterAlign = allThumbnailsFitInView && align === Alignment.CENTER;
  const shouldStartAlign = allThumbnailsFitInView && align === Alignment.START;
  return classNames(s.swiperWrapper, {
    [s.swiperWrapperCenterAlignment]: shouldCenterAlign,
    [s.swiperWrapperStartAlignment]: shouldStartAlign,
    [s.swiperWrapperVerticalSSR]: isVertical,
  });
}

function computeSwiperParams(isMainMediaNavArrowsEnabled: boolean, isMobile: boolean, isVertical: boolean) {
  const modules = isMobile ? [A11y] : [Navigation, A11y];
  const swiperModules = isMainMediaNavArrowsEnabled ? modules : [Navigation, A11y];
  let swiperNavigation;
  if (!isMainMediaNavArrowsEnabled) {
    swiperNavigation = {prevEl: `#${PREV_ARROW_BUTTON_ID}`, nextEl: `#${NEXT_ARROW_BUTTON_ID}`};
  } else {
    swiperNavigation = isMobile ? undefined : {nextEl: `#${NEXT_ARROW_BUTTON_ID}`};
  }
  const allowTouchMove = isMainMediaNavArrowsEnabled && isMobile;
  const direction: SwiperOptions['direction'] = isVertical ? 'vertical' : 'horizontal';
  const showPrevArrow = !isMainMediaNavArrowsEnabled;
  const showNextArrow = !isMainMediaNavArrowsEnabled || !isMobile;
  return {swiperModules, swiperNavigation, allowTouchMove, direction, showPrevArrow, showNextArrow};
}

export enum ProductMediaSwiperDataHooks {
  Root = 'product-media-swiper-root',
  PrevArrow = 'product-media-swiper-prev-arrow',
  NextArrow = 'product-media-swiper-next-arrow',
}
export interface ProductMediaSwiperProps extends ProvidedGlobalProps, IProvidedTranslationProps {
  media: IMediaItem[];
  isVertical: boolean;
  align: Alignment;
}

const ProductMediaSwiperComponent: FC<ProductMediaSwiperProps> = ({
  media,
  isVertical,
  align,
  t,
  globals: {useExperiments, styles},
}) => {
  const {changeSelectedIndex, selectedIndex} = useProductGallery();
  const [swiper, setSwiper] = useState<SwiperClass>(null);
  const {prevNavigation, nextNavigation, handleKeyDown} = useSwiperNavigation(media.length, swiper, isVertical);

  const hasVisibleArrows = useHasVisibleArrows(swiper);
  const slidesOverflow = useSlidesOverflow(swiper);
  const isMainMediaNavArrowsEnabled = useExperiments.enabled(SPECS.ProductPageMainMediaNavigationArrows);

  const thumbnailsOverflow = isMainMediaNavArrowsEnabled ? slidesOverflow : hasVisibleArrows;

  const {isRTL, isMobile} = useEnvironment();

  useEffect(() => {
    const shouldSlideToSelectedIndex = isMainMediaNavArrowsEnabled && slidesOverflow && !isMobile;

    if (shouldSlideToSelectedIndex) {
      swiper?.slideToLoop(selectedIndex);
    }
  }, [selectedIndex, isMainMediaNavArrowsEnabled, slidesOverflow, swiper, isMobile]);

  const swiperRootClasses = useMemo(
    () =>
      classNames(s.swiperRoot, {
        [s.swiperRootHorizontal]: !isVertical,
        [s.swiperRootVertical]: isVertical,
        [s.shouldRemovePrevArrow]: isMainMediaNavArrowsEnabled,
      }),
    [isVertical, isMainMediaNavArrowsEnabled]
  );

  const swiperWrapperClasses = computeSwiperWrapperClasses(isVertical, thumbnailsOverflow, align);

  /* istanbul ignore next */
  useEffect(() => {
    if (!swiper?.wrapperEl) {
      return;
    }
    setTimeout(() => swiper?.wrapperEl?.classList.remove(s.swiperWrapperVerticalSSR), 0);
  }, [swiper]);

  const swiperSlideClasses = useMemo(() => {
    return classNames(s.swiperSlide, s.swiperSlideThumbnail, {
      [s.swiperSlideThumbnailHorizontal]: !isVertical,
      [s.swiperSlideThumbnailVertical]: isVertical,
    });
  }, [isVertical]);

  const thumbnailsImageRatio = styles.get(stylesParams.productPage_thumbnailImageRatio);
  const thumbnailsSize = styles.get(stylesParams.productPage_thumbnailsSize);
  const thumbnailsSpacing = styles.get(stylesParams.productPage_thumbnailsSpacing);
  const isThumbnailsDesignViewerEnabled = useExperiments.enabled(SPECS.ThumbnailsDesignViewer);

  const inlineCssVars = computeInlineCssVars(
    isThumbnailsDesignViewerEnabled,
    media,
    thumbnailsSize,
    thumbnailsSpacing,
    thumbnailsImageRatio
  );
  const {thumbnailsWidth, thumbnailsHeight} = computeThumbnailDimensions(
    isThumbnailsDesignViewerEnabled,
    thumbnailsSize,
    thumbnailsImageRatio,
    media
  );
  const {swiperModules, swiperNavigation, allowTouchMove, direction, showPrevArrow, showNextArrow} =
    computeSwiperParams(isMainMediaNavArrowsEnabled, isMobile, isVertical);

  return (
    <div
      data-hook={ProductMediaSwiperDataHooks.Root}
      style={inlineCssVars as CSSProperties}
      className={classNames(s.root, {[s.rootVertical]: isVertical})}
      {...(thumbnailsOverflow && {onKeyDown: handleKeyDown})}>
      {showPrevArrow && (
        <ArrowButton
          id={PREV_ARROW_BUTTON_ID}
          dataHook={ProductMediaSwiperDataHooks.PrevArrow}
          onClick={prevNavigation}
          isVertical={isVertical}
          direction={getArrowDirection(true, isVertical, isRTL)}
        />
      )}
      <Swiper
        onSwiper={setSwiper}
        speed={SWIPER_SPEED}
        direction={direction}
        className={swiperRootClasses}
        wrapperClass={swiperWrapperClasses}
        modules={swiperModules}
        navigation={swiperNavigation}
        a11y={{
          prevSlideMessage: t('productPage.slideGallery.previous.ariaLabel'),
          nextSlideMessage: t('productPage.slideGallery.next.ariaLabel'),
        }}
        loop={true}
        slidesPerView="auto"
        watchSlidesProgress
        allowTouchMove={allowTouchMove}
        spaceBetween={0}>
        {media.map((mediaItem, index) => (
          <SwiperSlide className={swiperSlideClasses} key={index} onClick={() => changeSelectedIndex(index)}>
            <Thumbnail
              mediaItem={mediaItem}
              index={index}
              thumbnailsWidth={thumbnailsWidth}
              thumbnailsHeight={thumbnailsHeight}
            />
          </SwiperSlide>
        ))}
      </Swiper>
      {showNextArrow && (
        <ArrowButton
          id={NEXT_ARROW_BUTTON_ID}
          dataHook={ProductMediaSwiperDataHooks.NextArrow}
          onClick={nextNavigation}
          isVertical={isVertical}
          direction={getArrowDirection(false, isVertical, isRTL)}
        />
      )}
    </div>
  );
};

export const ProductMediaSwiper = withGlobalProps(withTranslations('globals.texts')(ProductMediaSwiperComponent));
